import { GoogleMap, useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_API_KEY } from '../../settings';
import GoogleMapsErrorBoundary from '../../GoogleMapsErrorBoundary';
import React, { useEffect, useRef } from 'react';
import { useReduxDispatch } from '../../Store/reduxHooks';
import { setIsMapLoaded } from '../../Store/reducers/Common';
import PALoader from '../PALoader';

interface MapProps {
  children: React.ReactNode;
  id: string;
  containerStyle?: any | null;
  center: { lat: number; lng: number };
  onLoad: (map: google.maps.Map) => void;
  onUnmount: (map: google.maps.Map) => void;
  zoom: number;
  options: google.maps.MapOptions;
  onClick?: (event: google.maps.MapMouseEvent) => void | null;
  testId?: string | '';
  onMouseUp?: (event: google.maps.MapMouseEvent) => void | null;
  onMouseDown?: (event: google.maps.MapMouseEvent) => void | null;
  onMouseMove?: (event: google.maps.MapMouseEvent) => void | null;
}

const libraries: any = ['drawing', 'places', 'marker'];

// Define static configs outside component to prevent recreation
let staticMapTypeControlOptions: any = null;
let staticDrawingControlOptions: any = null;

const Map: React.FC<MapProps> = (props) => {
  const {
    children,
    id,
    containerStyle,
    center,
    onLoad,
    onUnmount,
    zoom,
    options,
    onClick,
    testId,
    onMouseUp,
    onMouseDown,
    onMouseMove
  } = props;

  const dispatch = useReduxDispatch();

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: libraries
  });

  useEffect(() => {
    dispatch(setIsMapLoaded({ isMapLoaded: isLoaded }));
  }, [isLoaded]);

  if (isLoaded && !staticMapTypeControlOptions) {
    staticMapTypeControlOptions = {
      position: window.google.maps.ControlPosition.BOTTOM_LEFT,
      mapTypeIds: ['roadmap', 'satellite', 'terrain', 'hybrid']
    };
  }

  if (isLoaded && !staticDrawingControlOptions) {
    staticDrawingControlOptions = {
      position: window.google.maps.ControlPosition.TOP_LEFT,
      drawingModes: [window.google.maps.drawing.OverlayType.POLYGON]
    };
  }

  const mapOptionsRef = useRef<any>(null);

  if (!mapOptionsRef.current && isLoaded) {
    mapOptionsRef.current = {
      ...options,
      mapId: 'pa-connect-map',
      draggableCursor: options?.draggableCursor ?? undefined,
      maxZoom: 18,
      mapTypeControlOptions: staticMapTypeControlOptions,
      fullscreenControl: true
    };
  }

  return isLoaded ? (
    <GoogleMapsErrorBoundary>
      <GoogleMap
        id={id}
        mapContainerStyle={containerStyle}
        center={center}
        onLoad={onLoad}
        onUnmount={onUnmount}
        zoom={zoom}
        options={mapOptionsRef.current}
        onClick={onClick}
        data-testid={testId}
        onMouseUp={onMouseUp}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
      >
        {children}
      </GoogleMap>
    </GoogleMapsErrorBoundary>
  ) : (
    <PALoader />
  );
};

export default Map;
